import { useEffect, useRef } from 'react';
import { useUsageLogService } from '@actschurch/ccms-sdk';
import { useEnrichedAuth } from '@/hooks/useEnrichedAuth';
import { migrateAnonymousProgressTo } from '@/utils/progressStore';
import { getAnonymousId } from '@/utils/anonymousId';

/**
 * Mounted once near the root; watches the auth state and, the first time the
 * viewer becomes authenticated, hands off any anonymous progress to their
 * account:
 *
 *  1. Local: copies the <c>ccms:progress:anon:&lt;anonId&gt;</c> blob and
 *     <c>ccms:notes:anon:&lt;anonId&gt;:*</c> keys onto the user-keyed
 *     namespace so the course-progress card and notes editor read them under
 *     the new owner. Existing user-keyed entries always win — we never
 *     overwrite progress earned on another device.
 *
 *  2. Remote: posts the anonymousId + userId to <c>UsageLogService.linkAnonymous</c>
 *     so the analytics rows attached to the visitor's anon-id get re-keyed
 *     server-side. Best effort — fails quietly if the endpoint is offline.
 *
 * Renders nothing.
 */
export function AnonymousProgressMigrator() {
  const { isAuthenticated, user } = useEnrichedAuth();
  const usageLogService = useUsageLogService();
  const handledRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated) return;
    const userId = (user as { userId?: string; id?: string } | null)?.userId
      ?? (user as { id?: string } | null)?.id
      ?? null;
    if (!userId) return;
    if (handledRef.current === userId) return;
    handledRef.current = userId;

    // Local store hand-off.
    try {
      migrateAnonymousProgressTo(userId);
    } catch {
      /* noop */
    }

    // Server-side analytics merge — best effort.
    try {
      const anonymousId = getAnonymousId();
      // The endpoint is auth-required; the SDK attaches the bearer
      // automatically. Swallow the promise so a transient failure doesn't
      // block render.
      const svc = usageLogService as unknown as {
        linkAnonymous?: (payload: { anonymousId: string; userId: string }) => Promise<unknown>;
      };
      svc.linkAnonymous?.({ anonymousId, userId })?.catch(() => {});
    } catch {
      /* noop */
    }
  }, [isAuthenticated, user, usageLogService]);

  return null;
}
